import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CloudService, SqsQueue, ServiceCardData } from '../../core/services/cloud.service';
import { ToastService } from '../../core/services/toast.service';
import { MonitoringService } from '../../core/services/monitoring.service';
import {
  CloudFilters,
  FacetDef,
  SortOption,
  parseFiltersFromParams,
  filtersToParams,
} from './cloud-filter.model';
import { MetricColumn } from './cloud-metrics-panel.component';
import { ActivityItem, findingsToActivity, sortActivity } from './cloud-activity.model';

type Tab = 'overview' | 'queues' | 'activity';

const HISTORY_METRICS: MetricColumn[] = [
  { key: 'visible',    label: 'Backlog'    },
  { key: 'notVisible', label: 'In flight'  },
  { key: 'oldestAge',  label: 'Oldest (s)' },
];

const DEFAULT_SORT = 'backlog-desc';
const FACET_KEYS   = ['type', 'monitoring'];

const SORT_OPTIONS: SortOption[] = [
  { value: 'backlog-desc',    label: 'Largest backlog'  },
  { value: 'name-asc',        label: 'Name A → Z'       },
  { value: 'name-desc',       label: 'Name Z → A'       },
  { value: 'monitored-first', label: 'Monitored first'  },
];

const FACETS: FacetDef[] = [
  {
    key: 'type',
    label: 'Type',
    options: [
      { value: 'standard', label: 'Standard'     },
      { value: 'fifo',     label: 'FIFO'         },
      { value: 'dlq',      label: 'Dead-letter'  },
    ],
  },
  {
    key: 'monitoring',
    label: 'Monitoring',
    options: [
      { value: 'monitored',   label: 'Monitored'     },
      { value: 'unmonitored', label: 'Not monitored' },
    ],
  },
];

/**
 * SQS monitoring is at the queue level (CloudWatch QueueName dimension) —
 * message backlog and oldest-message age. The monitored resource identifier is
 * the queue name. Dead-letter queues are flagged via reverse redrive-policy
 * lookup during discovery; per-message inspection is not supported in v1.
 */
@Component({
  selector: 'app-sqs-detail',
  templateUrl: './sqs-detail.component.html',
})
export class SqsDetailComponent implements OnInit, OnDestroy {
  selectedTab: Tab = 'overview';
  queues: SqsQueue[] = [];
  loading = true;

  filters: CloudFilters = { query: '', facets: {}, sort: DEFAULT_SORT };

  readonly tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'queues',   label: 'Queues'   },
    { id: 'activity', label: 'Activity' },
  ];

  readonly facets      = FACETS;
  readonly sortOptions = SORT_OPTIONS;

  readonly serviceCard: ServiceCardData = { id: 'sqs', name: 'SQS', icon: 'inbox', route: '/cloud/sqs', status: 'healthy', metrics: [] };
  readonly historyMetrics = HISTORY_METRICS;

  drawerItemId: string | null = null;
  selectedId: string | null = null;

  activity: ActivityItem[] = [];
  activityLoading = true;
  refreshing = false;

  private sub?: Subscription;
  private routeSub?: Subscription;
  private findingsSub?: Subscription;

  constructor(
    private readonly cloud: CloudService,
    private readonly toast: ToastService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly monitoring: MonitoringService,
  ) {}

  ngOnInit(): void {
    this.cloud.loadItemPoliciesForService('sqs').subscribe();
    this.routeSub = this.route.queryParams.subscribe(params => {
      const p = params as Record<string, string>;
      this.filters = parseFiltersFromParams(p, DEFAULT_SORT, FACET_KEYS);
      this.selectedId = p['r'] ?? null;
    });
    this.loadData(false);
  }

  /** Loads the queue list (cache-aware), monitoring state (monitored count), and activity findings. */
  private loadData(bypass: boolean): void {
    this.sub?.unsubscribe();
    this.sub = this.cloud.getSqsQueues(bypass).subscribe(queues => {
      this.queues     = queues;
      this.loading    = false;
      this.refreshing = false;
    });
    this.cloud.loadMonitoringState().subscribe();
    this.findingsSub?.unsubscribe();
    this.findingsSub = this.monitoring.getFindings().subscribe(findings => {
      this.activityLoading = false;
      this.activity        = sortActivity(findingsToActivity(findings, 'SQS'));
    });
  }

  /** Manual refresh — bypass the cache and reload (guarded against double-clicks). */
  refresh(): void {
    if (this.refreshing) return;
    this.refreshing = true;
    this.loadData(true);
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.routeSub?.unsubscribe();
    this.findingsSub?.unsubscribe();
  }

  onActivitySelect(item: ActivityItem): void {
    this.selectedTab = 'queues';
    this.selectResource(item.resourceId);
  }

  // ── Overview counts (standardized: total + monitored, then service-specific) ──

  get totalCount():      number { return this.queues.length; }
  get monitoredCount():  number { return this.queues.filter(q => this.isMonitored(q.name)).length; }
  get backloggedCount(): number { return this.queues.filter(q => q.messagesVisible > 0).length; }
  get deadLetterCount(): number { return this.queues.filter(q => q.isDeadLetter).length; }

  // ── Filtered + sorted list ──────────────────────────────────────────────────

  get filteredQueues(): SqsQueue[] {
    let result = this.queues;

    const q = this.filters.query.toLowerCase().trim();
    if (q) result = result.filter(item => item.name.toLowerCase().includes(q));

    const types = this.filters.facets['type'] ?? [];
    if (types.length) {
      result = result.filter(item =>
        (types.includes('fifo')     && item.isFifo) ||
        (types.includes('standard') && !item.isFifo) ||
        (types.includes('dlq')      && item.isDeadLetter));
    }

    const monitoring = this.filters.facets['monitoring'] ?? [];
    if (monitoring.length) {
      result = result.filter(item => {
        const mon = this.isMonitored(item.name);
        return (monitoring.includes('monitored') && mon) || (monitoring.includes('unmonitored') && !mon);
      });
    }

    return this.sorted(result, this.filters.sort);
  }

  private sorted(queues: SqsQueue[], sort: string): SqsQueue[] {
    return [...queues].sort((a, b) => {
      switch (sort) {
        case 'name-asc':        return a.name.localeCompare(b.name);
        case 'name-desc':       return b.name.localeCompare(a.name);
        case 'monitored-first': {
          const aM = this.isMonitored(a.name) ? 0 : 1;
          const bM = this.isMonitored(b.name) ? 0 : 1;
          return aM - bM || a.name.localeCompare(b.name);
        }
        default: return b.messagesVisible - a.messagesVisible || a.name.localeCompare(b.name); // backlog-desc
      }
    });
  }

  onFiltersChange(f: CloudFilters): void {
    this.router.navigate([], {
      relativeTo:          this.route,
      queryParams:         filtersToParams(f, FACET_KEYS),
      queryParamsHandling: 'merge',
      replaceUrl:          true,
    });
  }

  // ── Display helpers ─────────────────────────────────────────────────────────

  typeLabel(queue: SqsQueue): string {
    if (queue.isDeadLetter) return 'Dead-letter';
    return queue.isFifo ? 'FIFO' : 'Standard';
  }

  typeClass(queue: SqsQueue): string {
    if (queue.isDeadLetter) return 'bg-amber-100 text-amber-700';
    return queue.isFifo ? 'bg-violet-100 text-violet-700' : 'bg-slate-100 text-slate-500';
  }

  // ── Monitoring ──────────────────────────────────────────────────────────────

  get quotaUsage() { return this.cloud.getQuotaUsage(); }
  get canMonitorMore(): boolean { return this.cloud.canEnableMoreMonitoring(); }

  isMonitored(queueName: string): boolean { return this.cloud.isMonitored('sqs', queueName); }

  toggleMonitoring(queueName: string): void {
    const current = this.cloud.isMonitored('sqs', queueName);
    if (!current && !this.cloud.canEnableMoreMonitoring()) {
      this.toast.show(this.cloud.getQuotaExceededMessage(), 'error');
      return;
    }
    this.cloud.toggleMonitoring('sqs', queueName, !current).subscribe({
      error: err => {
        if (err.status === 403) {
          const msg = err.error?.code === 'QUOTA_EXCEEDED'
            ? this.cloud.getQuotaExceededMessage()
            : 'Only org owners and admins can configure monitoring.';
          this.toast.show(msg, 'error');
        }
      },
    });
  }

  // ── Metric-history drill-down ───────────────────────────────────────────────

  selectResource(queueName: string): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: queueName }, queryParamsHandling: 'merge', replaceUrl: true });
  }
  closeResource(): void {
    this.router.navigate([], { relativeTo: this.route, queryParams: { r: null }, queryParamsHandling: 'merge', replaceUrl: true });
  }

  // ── Item settings drawer ────────────────────────────────────────────────────

  openItemSettings(queueName: string): void  { this.drawerItemId = queueName; }
  closeItemSettings(): void                   { this.drawerItemId = null; }

  itemOverrideSummary(queueName: string): string {
    return this.cloud.getItemOverrideSummary('sqs', queueName);
  }
}
